// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Hearsay — the rules. Pure, seeded, and knowing nothing about the DOM, the clock or the network.
//
// TWO INVARIANTS HOLD THIS FILE TOGETHER, and both exist because of bugs this fleet has already
// shipped:
//
//  1. NO BRANCH IS EVER TAKEN ON HIDDEN INFORMATION OUTSIDE THE HOST. A sibling game desynced
//     permanently because its turn order skipped a seat that "could not legally act" — a predicate
//     that depends on that seat's hidden hand. Here, everything that decides the SHAPE of play —
//     whose turn it is, whether a target exists, whether a card resolves at all — reads only alive
//     flags, shields and the discard pile, all of which are public. The only hidden reads are inside
//     a resolution the host performs and then announces.
//
//  2. THE PUBLIC VIEW CONTAINS NO HIDDEN RANK. `events` below is the FULL stream, for the host, the
//     simulator and the audit. `publicView()` redacts it. tests/leak.test.ts walks the serialised
//     public view of thousands of plies and fails on any rank that the rules say a guest may not see.

import { makeRng, shuffle, type Rng } from '@ben-gy/game-engine/rng';
import {
  ADVOCATE, BAILIFF, CLERK, JUDGE, JUDGE_FORCES, RECUSAL, RETRIAL, VERDICT, WITNESS,
  deckCards, deckSize, type DeckSpec,
} from './cards';
import { modeOf, seatsFor, type Mode, type ModeId } from './modes';

export interface Card {
  /** Stable within a hearing, so a trade moves an identity rather than a value. */
  id: number;
  rank: number;
}

export type OutCause = 'named' | 'dismissed' | 'verdict' | 'left';

/** What a missed call tells the caller, and only the caller. See MISS_HINT_IS_PRIVATE in cards.ts. */
export type Hint = 'higher' | 'lower' | null;

export interface Seat {
  id: string;
  name: string;
  bot: boolean;
  hand: Card[];
  /** Every rank this seat has played or been made to discard, in order. PUBLIC. */
  discards: number[];
  alive: boolean;
  /** Recusal: cleared at the start of this seat's next turn. PUBLIC. */
  shielded: boolean;
  /** Who this seat backed from the gallery after being put out. PUBLIC. */
  backed: string | null;
  outCause: OutCause | null;
  /** Ply at which they were put out, for the results breakdown. */
  outPly: number | null;
}

export interface SeatSpec {
  id: string;
  name: string;
  bot: boolean;
}

export type Ev =
  | { t: 'deal'; deck: DeckSpec; hands: { id: string; ranks: number[] }[]; aside: number; open: number[] }
  | { t: 'play'; ply: number; by: string; rank: number; target: string | null; named: number | null; forced: boolean }
  | { t: 'noeffect'; ply: number; by: string; rank: number; why: 'no-target' | 'inert' }
  | { t: 'peek'; by: string; target: string; ranks: number[] }
  | {
      t: 'call';
      by: string;
      target: string;
      named: number;
      hit: boolean;
      held: number[];
      /** On a miss: whether their top rank is above or below the named one. PRIVATE to `by`. */
      hint: Hint;
    }
  | { t: 'compare'; a: string; b: string; aRanks: number[]; bRanks: number[]; out: string | null }
  | { t: 'swap'; a: string; b: string; aRanks: number[]; bRanks: number[] }
  | { t: 'redraw'; target: string; discarded: number[]; drew: number[]; fromAside: boolean }
  | { t: 'shield'; id: string; on: boolean }
  | { t: 'out'; id: string; cause: OutCause; rank: number | null; ply: number }
  | { t: 'back'; id: string; on: string }
  | {
      t: 'close';
      reason: 'last' | 'deck';
      winners: string[];
      held: { id: string; ranks: number[] }[];
      /** The one card nobody could ever account for, revealed now. Principle #9's "what you missed". */
      aside: number;
    };

export interface LogLine {
  text: string;
  kind: 'info' | 'out' | 'hit' | 'miss';
}

export interface GameState {
  seed: number;
  modeId: ModeId;
  players: number;
  seats: Seat[];
  /** Hidden draw pile, top at the end. */
  deck: Card[];
  /** Hidden, set aside before the deal — the reason a perfect count still leaves one doubt. */
  aside: Card | null;
  /** Face-up beside the docket. PUBLIC. */
  open: number[];
  /** The full composition dealt from, so the docket can be computed by anyone. PUBLIC. */
  spec: DeckSpec;
  turn: string;
  ply: number;
  phase: 'play' | 'over';
  reason: 'last' | 'deck' | null;
  winners: string[];
  log: LogLine[];
  events: Ev[];
}

// ── helpers ─────────────────────────────────────────────────────────────────────────────────────

export const seatOf = (s: GameState, id: string): Seat | undefined => s.seats.find((x) => x.id === id);
export const aliveSeats = (s: GameState): Seat[] => s.seats.filter((x) => x.alive);
export const topRank = (seat: Seat): number => seat.hand.reduce((a, c) => Math.max(a, c.rank), 0);

/** Ranks a Bailiff may be told to name. Never its own rank — that is what makes it the common card. */
export const NAMEABLE: readonly number[] = [CLERK, WITNESS, RECUSAL, RETRIAL, ADVOCATE, JUDGE, VERDICT];

/**
 * Whether a card needs somebody to point at, and whether it may point at its own player.
 * Both are functions of the RANK alone — public, so every peer computes the same answer.
 */
export const needsTarget = (rank: number): boolean =>
  rank === BAILIFF || rank === CLERK || rank === WITNESS || rank === RETRIAL || rank === ADVOCATE;
export const mayTargetSelf = (rank: number): boolean => rank === RETRIAL;

/**
 * Who a card played by `from` may point at. Reads only alive flags and shields — both PUBLIC — so
 * this is safe for a guest to evaluate for its own UI and can never disagree with the host.
 */
export interface TargetableSeat {
  id: string;
  alive: boolean;
  shielded: boolean;
}

export function targetsFrom(seats: readonly TargetableSeat[], from: string, rank: number): string[] {
  if (!needsTarget(rank)) return [];
  const out = seats.filter((x) => x.alive && !x.shielded && x.id !== from).map((x) => x.id);
  if (mayTargetSelf(rank) && seats.some((x) => x.id === from && x.alive)) out.push(from);
  return out;
}

export const legalTargets = (s: GameState, from: string, rank: number): string[] =>
  targetsFrom(s.seats, from, rank);

/**
 * The Judge's forced discard, as a pure function of a hand.
 *
 * This is the single most load-bearing deduction in the game — somebody discarding the Judge told
 * you they were holding the Advocate or a Retrial — so it is asserted independently, from the
 * constants rather than from this function, in tests/mechanism.test.ts.
 */
export function forcedCard(hand: readonly Card[]): Card | null {
  const judge = hand.find((c) => c.rank === JUDGE);
  if (!judge) return null;
  return hand.some((c) => JUDGE_FORCES.includes(c.rank)) ? judge : null;
}

/** Every card this seat could legally play right now. */
export function playableCards(s: GameState, id: string): Card[] {
  const seat = seatOf(s, id);
  if (!seat || !seat.alive || s.phase !== 'play' || s.turn !== id) return [];
  const forced = forcedCard(seat.hand);
  return forced ? [forced] : seat.hand.slice();
}

/** The same question, answered from what a GUEST holds: its own cards plus the public snapshot. */
export function playableFrom(p: PublicState, hand: readonly Card[], id: string): Card[] {
  const seat = p.seats.find((x) => x.id === id);
  if (!seat || !seat.alive || p.phase !== 'play' || p.turn !== id) return [];
  const forced = forcedCard(hand);
  return forced ? [forced] : hand.slice();
}

/**
 * May this seat back a rival from the gallery?
 *
 * Only while TWO rivals are still standing. Letting an eliminated player wait until one remained
 * would hand them a free point for a choice with no doubt in it, which is the opposite of the
 * deduction the bonus is meant to reward.
 */
export function canBack(s: GameState, id: string): boolean {
  const seat = seatOf(s, id);
  return !!seat && !seat.alive && seat.backed === null && s.phase === 'play' && aliveSeats(s).length >= 2;
}

const say = (s: GameState, text: string, kind: LogLine['kind'] = 'info'): void => {
  s.log.push({ text, kind });
  if (s.log.length > 60) s.log.shift();
};

// ── dealing ─────────────────────────────────────────────────────────────────────────────────────

export function deal(seed: number, modeId: ModeId, specs: readonly SeatSpec[]): GameState {
  const mode = modeOf(modeId);
  const players = seatsFor(mode, specs.length);
  const spec = mode.deckFor(players);
  const rng: Rng = makeRng(`${seed}:${modeId}:${players}`);
  const order = shuffle(rng, deckCards(spec));
  const deck: Card[] = order.map((rank, id) => ({ id, rank }));

  const aside = deck.pop() ?? null;
  const open: number[] = [];
  for (let k = 0; k < mode.openBurns(players); k++) {
    const c = deck.pop();
    if (c) open.push(c.rank);
  }
  open.sort((a, b) => a - b);

  const seats: Seat[] = specs.slice(0, players).map((sp) => ({
    id: sp.id,
    name: sp.name,
    bot: sp.bot,
    hand: [],
    discards: [],
    alive: true,
    shielded: false,
    backed: null,
    outCause: null,
    outPly: null,
  }));
  for (let k = 0; k < mode.handSize; k++) {
    for (const seat of seats) {
      const c = deck.pop();
      if (c) seat.hand.push(c);
    }
  }

  const s: GameState = {
    seed,
    modeId: mode.id,
    players,
    seats,
    deck,
    aside,
    open,
    spec,
    turn: seats[0]?.id ?? '',
    ply: 0,
    phase: 'play',
    reason: null,
    winners: [],
    log: [],
    events: [],
  };
  s.events.push({
    t: 'deal',
    deck: spec,
    hands: seats.map((x) => ({ id: x.id, ranks: x.hand.map((c) => c.rank) })),
    aside: aside?.rank ?? 0,
    open: open.slice(),
  });
  say(s, 'The hearing opens.');
  // The first actor draws before their turn, exactly as every later actor does.
  drawFor(s, s.turn);
  return s;
}

function drawFor(s: GameState, id: string): void {
  const seat = seatOf(s, id);
  if (!seat) return;
  const mode = modeOf(s.modeId);
  while (seat.hand.length <= mode.handSize && s.deck.length > 0) {
    const c = s.deck.pop();
    if (c) seat.hand.push(c);
  }
}

// ── the turn ────────────────────────────────────────────────────────────────────────────────────

export interface PlayInput {
  cardId: number;
  target?: string | null;
  named?: number | null;
}

/**
 * Play one card. Returns a NEW state when the move was legal, and the SAME state when it was not —
 * so an illegal action off the wire is a no-op rather than a crash or a divergence.
 */
export function play(prev: GameState, from: string, input: PlayInput): GameState {
  if (prev.phase !== 'play' || prev.turn !== from) return prev;
  const s = clone(prev);
  const seat = seatOf(s, from);
  if (!seat || !seat.alive) return prev;

  const card = seat.hand.find((c) => c.id === input.cardId);
  if (!card) return prev;
  const forced = forcedCard(seat.hand);
  if (forced && forced.id !== card.id) return prev;

  const targets = legalTargets(s, from, card.rank);
  let target = input.target ?? null;
  if (needsTarget(card.rank)) {
    if (targets.length === 0) target = null;
    else if (!target || !targets.includes(target)) return prev;
  } else {
    target = null;
  }
  if (card.rank === BAILIFF && target !== null) {
    const named = input.named ?? 0;
    if (!NAMEABLE.includes(named)) return prev;
  }

  // Committed. Everything from here is announced.
  seat.hand = seat.hand.filter((c) => c.id !== card.id);
  seat.discards.push(card.rank);
  s.ply++;
  s.events.push({
    t: 'play',
    ply: s.ply,
    by: from,
    rank: card.rank,
    target,
    named: card.rank === BAILIFF ? (input.named ?? null) : null,
    forced: !!forced,
  });

  if (card.rank === VERDICT) {
    knockOut(s, from, 'verdict', VERDICT);
  } else if (needsTarget(card.rank) && target === null) {
    s.events.push({ t: 'noeffect', ply: s.ply, by: from, rank: card.rank, why: 'no-target' });
    say(s, `${seat.name} plays ${rankPhrase(card.rank)} — nobody to point it at.`);
  } else {
    resolve(s, seat, card.rank, target, input.named ?? 0);
  }

  advance(s, from);
  return s;
}

function resolve(s: GameState, actor: Seat, rank: number, targetId: string | null, named: number): void {
  const target = targetId ? seatOf(s, targetId) : null;

  switch (rank) {
    case BAILIFF: {
      if (!target) return;
      const held = target.hand.map((c) => c.rank);
      const hit = held.includes(named);
      // The hint reads off the TOP rank, so it means the same thing whether a seat holds one card
      // or two. Only the caller ever receives it.
      const hint: Hint = hit ? null : topRank(target) > named ? 'higher' : 'lower';
      s.events.push({ t: 'call', by: actor.id, target: target.id, named, hit, held, hint });
      if (hit) {
        say(s, `${actor.name} names ${rankPhrase(named)} — and ${target.name} is holding it.`, 'hit');
        knockOut(s, target.id, 'named', named);
      } else {
        say(s, `${actor.name} names ${rankPhrase(named)} against ${target.name}. Not it.`, 'miss');
      }
      return;
    }
    case CLERK: {
      if (!target) return;
      s.events.push({ t: 'peek', by: actor.id, target: target.id, ranks: target.hand.map((c) => c.rank) });
      say(s, `${actor.name} reads ${target.name}'s testimony.`);
      return;
    }
    case WITNESS: {
      if (!target) return;
      const mine = topRank(actor);
      const theirs = topRank(target);
      const loser = mine === theirs ? null : mine > theirs ? target.id : actor.id;
      s.events.push({
        t: 'compare',
        a: actor.id,
        b: target.id,
        aRanks: actor.hand.map((c) => c.rank),
        bRanks: target.hand.map((c) => c.rank),
        out: loser,
      });
      if (!loser) {
        say(s, `${actor.name} and ${target.name} match, word for word. Nothing comes of it.`);
      } else {
        const lost = seatOf(s, loser)!;
        say(s, `${actor.name} takes the stand against ${target.name}. ${lost.name} is dismissed.`, 'out');
        knockOut(s, loser, 'dismissed', topRank(lost));
      }
      return;
    }
    case RECUSAL: {
      actor.shielded = true;
      s.events.push({ t: 'shield', id: actor.id, on: true });
      say(s, `${actor.name} steps off the record.`);
      return;
    }
    case RETRIAL: {
      if (!target) return;
      const discarded = target.hand.map((c) => c.rank);
      const hadVerdict = discarded.includes(VERDICT);
      for (const r of discarded) target.discards.push(r);
      target.hand = [];
      const drew: number[] = [];
      let fromAside = false;
      const mode = modeOf(s.modeId);
      while (target.hand.length < mode.handSize) {
        let c = s.deck.pop() ?? null;
        if (!c && s.aside) {
          c = s.aside;
          s.aside = null;
          fromAside = true;
        }
        if (!c) break;
        target.hand.push(c);
        drew.push(c.rank);
      }
      s.events.push({ t: 'redraw', target: target.id, discarded, drew, fromAside });
      say(
        s,
        actor.id === target.id
          ? `${actor.name} starts again.`
          : `${actor.name} sends ${target.name} back to the start.`,
      );
      if (hadVerdict) knockOut(s, target.id, 'verdict', VERDICT);
      return;
    }
    case ADVOCATE: {
      if (!target) return;
      const a = actor.hand;
      const b = target.hand;
      s.events.push({
        t: 'swap',
        a: actor.id,
        b: target.id,
        aRanks: a.map((c) => c.rank),
        bRanks: b.map((c) => c.rank),
      });
      actor.hand = b;
      target.hand = a;
      say(s, `${actor.name} and ${target.name} exchange papers.`);
      return;
    }
    case JUDGE: {
      s.events.push({ t: 'noeffect', ply: s.ply, by: actor.id, rank: JUDGE, why: 'inert' });
      say(s, `${actor.name} stands the Judge down.`);
      return;
    }
    default:
      return;
  }
}

function knockOut(s: GameState, id: string, cause: OutCause, rank: number | null): void {
  const seat = seatOf(s, id);
  if (!seat || !seat.alive) return;
  seat.alive = false;
  seat.outCause = cause;
  seat.outPly = s.ply;
  seat.shielded = false;
  for (const c of seat.hand) seat.discards.push(c.rank);
  seat.hand = [];
  s.events.push({ t: 'out', id, cause, rank, ply: s.ply });
  say(s, `${seat.name} is out of the hearing.`, 'out');
}

/** Back a rival from the gallery. Legal only while two rivals still stand — see canBack(). */
export function back(prev: GameState, from: string, on: string): GameState {
  if (!canBack(prev, from)) return prev;
  const s = clone(prev);
  const pick = seatOf(s, on);
  if (!pick || !pick.alive || on === from) return prev;
  seatOf(s, from)!.backed = on;
  s.events.push({ t: 'back', id: from, on });
  return s;
}

/**
 * End the hearing if departures have collapsed it to a single survivor. A WALKOVER: they win.
 *
 * Needed because a promoted host rebuilds the table with the departed seats ALREADY marked dead, so
 * the usual "forfeit them and let advance() notice" path is a no-op — every seat it would forfeit is
 * out already. Found in a two-tab smoke test, where the survivor of a two-player hearing was left on
 * a live board with nobody to play against and no way for it to end. No unit test saw it, because
 * every unit test forfeited a seat that was still alive.
 */
export function closeIfCollapsed(prev: GameState): GameState {
  if (prev.phase !== 'play' || aliveSeats(prev).length > 1) return prev;
  const s = clone(prev);
  close(s, 'last');
  return s;
}

/** A seat whose player has gone. Their testimony leaves with them; the hearing carries on. */
export function forfeit(prev: GameState, id: string): GameState {
  const seat = seatOf(prev, id);
  if (!seat || !seat.alive || prev.phase !== 'play') return prev;
  const s = clone(prev);
  const wasActing = s.turn === id;
  knockOut(s, id, 'left', null);
  // Only hand the turn on if it was actually theirs; otherwise the hearing carries on where it was,
  // and we still have to check whether their leaving ended it.
  if (wasActing) advance(s, id);
  else if (aliveSeats(s).length <= 1) close(s, 'last');
  return s;
}

function advance(s: GameState, after: string): void {
  if (s.phase !== 'play') return;
  const alive = aliveSeats(s);
  if (alive.length <= 1) {
    close(s, 'last');
    return;
  }
  const order = s.seats.map((x) => x.id);
  const start = Math.max(0, order.indexOf(after));
  for (let k = 1; k <= order.length; k++) {
    const next = s.seats[(start + k) % order.length];
    if (!next.alive) continue;
    if (s.deck.length === 0) {
      close(s, 'deck');
      return;
    }
    if (next.shielded) {
      next.shielded = false;
      s.events.push({ t: 'shield', id: next.id, on: false });
    }
    s.turn = next.id;
    drawFor(s, next.id);
    return;
  }
  close(s, 'deck');
}

function close(s: GameState, reason: 'last' | 'deck'): void {
  if (s.phase === 'over') return;
  s.phase = 'over';
  s.reason = reason;
  const alive = aliveSeats(s);
  if (reason === 'last') {
    s.winners = alive.map((x) => x.id);
  } else {
    const best = alive.reduce((a, x) => Math.max(a, topRank(x)), 0);
    s.winners = alive.filter((x) => topRank(x) === best).map((x) => x.id);
  }
  s.events.push({
    t: 'close',
    reason,
    winners: s.winners.slice(),
    held: s.seats.map((x) => ({ id: x.id, ranks: x.hand.map((c) => c.rank) })),
    aside: s.aside?.rank ?? 0,
  });
  say(
    s,
    reason === 'last'
      ? 'Everyone else is out. The hearing closes.'
      : 'The docket is empty. The strongest testimony stands.',
    'info',
  );
}

// ── scoring ─────────────────────────────────────────────────────────────────────────────────────

export interface Award {
  id: string;
  /** Standing earned by this seat for this hearing. */
  gained: number;
  /** Why: took the hearing, scored what they held, or backed the winner from the gallery. */
  won: boolean;
  held: number;
  backedRight: boolean;
}

/**
 * What a finished hearing pays.
 *
 * Deliberately a pure function of the FINISHED state and the mode, so the simulator, the host and
 * the results screen cannot disagree about a score — and so tests/mechanism.test.ts can re-derive it
 * from the mode constants without importing this function.
 */
export function awards(s: GameState, mode: Mode): Award[] {
  if (s.phase !== 'over') return [];
  const winners = new Set(s.winners);
  return s.seats.map((seat) => {
    const held = seat.alive ? topRank(seat) : 0;
    const won = winners.has(seat.id);
    const backedRight = seat.backed !== null && winners.has(seat.backed);
    let gained = 0;
    if (mode.scoring === 'win') {
      gained += won ? (s.reason === 'last' ? mode.lastAward : mode.deckAward) : 0;
    } else {
      gained += held;
    }
    if (backedRight) gained += mode.galleryAward;
    return { id: seat.id, gained, won, held, backedRight };
  });
}

// ── views ───────────────────────────────────────────────────────────────────────────────────────

export interface PublicSeat {
  id: string;
  name: string;
  bot: boolean;
  handCount: number;
  discards: number[];
  alive: boolean;
  shielded: boolean;
  backed: string | null;
  outCause: OutCause | null;
  outPly: number | null;
}

export type PublicEv =
  | { t: 'play'; ply: number; by: string; rank: number; target: string | null; named: number | null; forced: boolean }
  | { t: 'noeffect'; ply: number; by: string; rank: number; why: 'no-target' | 'inert' }
  | { t: 'peek'; by: string; target: string }
  | { t: 'call'; by: string; target: string; named: number; hit: boolean }
  | { t: 'compare'; a: string; b: string; out: string | null }
  | { t: 'swap'; a: string; b: string }
  | { t: 'redraw'; target: string; discarded: number[]; count: number }
  | { t: 'shield'; id: string; on: boolean }
  | { t: 'out'; id: string; cause: OutCause; rank: number | null; ply: number }
  | { t: 'back'; id: string; on: string }
  | {
      t: 'close';
      reason: 'last' | 'deck';
      winners: string[];
      held: { id: string; ranks: number[] }[];
      aside: number;
    };

export interface PublicState {
  /**
   * THE DEAL SEED IS NOT HERE, AND MUST NEVER BE PUT HERE.
   *
   * It was, briefly, and it defeated the entire hidden-information design in one field: the deal is
   * `deal(seed, mode, roster)`, the mode and the roster are public, so a guest with the seed could
   * re-run the deal in a console and read every hand and the whole deck order. Nothing else would
   * have looked wrong — the hands are still absent, every event is still redacted, and the leak test
   * was still green, because it was checking for hands rather than for the thing that generates them.
   *
   * Anything that needs a stable per-hearing value uses `round` and `ply`, which are public and
   * identical on every peer. tests/leak.test.ts now fails on `seed` by name.
   */
  modeId: ModeId;
  players: number;
  seats: PublicSeat[];
  deckCount: number;
  /** True while a card is still set aside. Its rank is never public until the hearing closes. */
  asideHeld: boolean;
  open: number[];
  spec: DeckSpec;
  turn: string;
  ply: number;
  phase: 'play' | 'over';
  reason: 'last' | 'deck' | null;
  winners: string[];
  log: LogLine[];
  events: PublicEv[];
  /** Match standing, carried by the session and pasted on so one snapshot paints the whole screen. */
  standing: Record<string, number>;
  round: number;
  target: number;
}

/**
 * What ONE seat has legitimately learned, and nothing else.
 *
 * Two shapes, because the game gives you two kinds of knowledge: `ranks` is certainty (you read it,
 * you stood against it, you handed it over), and `bound` is the private half-answer a missed call
 * buys you. The bot's belief state consumes exactly this and nothing more, which is what makes
 * "a bot denied the docket plays worse" a meaningful measurement rather than a rigged one.
 */
export interface Learned {
  seat: string;
  ranks: number[] | null;
  bound: { named: number; hint: 'higher' | 'lower' } | null;
  ply: number;
  how: 'read' | 'stood' | 'traded' | 'called';
}

export interface PrivateView {
  id: string;
  hand: Card[];
  learned: Learned[];
}

/**
 * Strip a full event to what the whole room may see.
 *
 * A `compare` is the interesting one: the ROOM sees that it happened and who lost, because both are
 * announced at the table — but never the two ranks, which only the two of them saw. `redraw`
 * publishes the discarded ranks (they hit the table face-up) and only the COUNT of what replaced
 * them.
 */
export function redact(e: Ev): PublicEv | null {
  switch (e.t) {
    case 'deal':
      return null;
    case 'peek':
      return { t: 'peek', by: e.by, target: e.target };
    case 'call':
      return { t: 'call', by: e.by, target: e.target, named: e.named, hit: e.hit };
    case 'compare':
      return { t: 'compare', a: e.a, b: e.b, out: e.out };
    case 'swap':
      return { t: 'swap', a: e.a, b: e.b };
    case 'redraw':
      return { t: 'redraw', target: e.target, discarded: e.discarded.slice(), count: e.drew.length };
    default:
      return e;
  }
}

export function publicView(
  s: GameState,
  extra: { standing: Record<string, number>; round: number; target: number },
): PublicState {
  return {
    modeId: s.modeId,
    players: s.players,
    seats: s.seats.map((x) => ({
      id: x.id,
      name: x.name,
      bot: x.bot,
      handCount: x.hand.length,
      discards: x.discards.slice(),
      alive: x.alive,
      shielded: x.shielded,
      backed: x.backed,
      outCause: x.outCause,
      outPly: x.outPly,
    })),
    deckCount: s.deck.length,
    asideHeld: s.aside !== null,
    open: s.open.slice(),
    spec: s.spec,
    turn: s.turn,
    ply: s.ply,
    phase: s.phase,
    reason: s.reason,
    winners: s.winners.slice(),
    log: s.log.slice(-14),
    // The stream is redacted, always. `deal` — the one event that names every hand at once — has no
    // public form at all and is dropped outright; nothing later reinstates it. Once the hearing is
    // over the close event carries every hand, which is exactly what the results screen needs and
    // which hides nothing by then.
    events: s.events.map(redact).filter((e): e is PublicEv => e !== null),
    standing: { ...extra.standing },
    round: extra.round,
    target: extra.target,
  };
}

export function privateView(s: GameState, id: string): PrivateView | null {
  const seat = seatOf(s, id);
  if (!seat) return null;
  const learned: Learned[] = [];
  let ply = 0;
  for (const e of s.events) {
    if (e.t === 'play' || e.t === 'noeffect' || e.t === 'out') ply = e.ply;
    const exact = (seat: string, ranks: number[], how: Learned['how']): void => {
      learned.push({ seat, ranks: ranks.slice(), bound: null, ply, how });
    };
    if (e.t === 'peek' && e.by === id) exact(e.target, e.ranks, 'read');
    else if (e.t === 'compare' && e.a === id) exact(e.b, e.bRanks, 'stood');
    else if (e.t === 'compare' && e.b === id) exact(e.a, e.aRanks, 'stood');
    // A trade hands your OWN cards over, so what you now know about them is what you just gave away.
    else if (e.t === 'swap' && e.a === id) exact(e.b, e.aRanks, 'traded');
    else if (e.t === 'swap' && e.b === id) exact(e.a, e.bRanks, 'traded');
    else if (e.t === 'call' && e.by === id && e.hint) {
      learned.push({ seat: e.target, ranks: null, bound: { named: e.named, hint: e.hint }, ply, how: 'called' });
    }
  }
  return { id, hand: seat.hand.map((c) => ({ ...c })), learned };
}

/**
 * The docket: how many of each rank are still unaccounted for, from PUBLIC information only.
 *
 * This is the whole skill of the game made visible, and it is computed the same way by every peer
 * and by the bot. Anything on the table — played, forced out with an eliminated player, or turned
 * face-up at the start — is gone from here. tests/balance.test.ts proves a bot denied this loses.
 */
export function docket(p: PublicState): number[] {
  const left = p.spec.slice();
  for (const seat of p.seats) for (const r of seat.discards) left[r] = Math.max(0, (left[r] ?? 0) - 1);
  for (const r of p.open) left[r] = Math.max(0, (left[r] ?? 0) - 1);
  return left;
}

/** Cards that are neither on the table nor in my own hand: the pool a rival's card is drawn from. */
export function unseen(p: PublicState, myHand: readonly number[]): number[] {
  const left = docket(p);
  for (const r of myHand) left[r] = Math.max(0, (left[r] ?? 0) - 1);
  return left;
}

export const rankName = (rank: number): string =>
  ['', 'Bailiff', 'Clerk', 'Witness', 'Recusal', 'Retrial', 'Advocate', 'The Judge', 'The Verdict'][rank] ?? '?';

/**
 * The card's name with its article already attached.
 *
 * Two of the eight carry "The" as part of the name, so every "the ${rankName(r)}" in the interface
 * came out as "the The Verdict". Composing the article here means there is one place to be wrong.
 */
export const rankPhrase = (rank: number): string => {
  const name = rankName(rank);
  return name.startsWith('The ') ? name : `the ${name}`;
};

export const specSize = (d: DeckSpec): number => deckSize(d);

/** The same rule, evaluated from what a GUEST can see. It must agree with legalTargets(), always. */
export const legalTargetsPub = (p: PublicState, from: string, rank: number): string[] =>
  targetsFrom(p.seats, from, rank);

/** The set-aside rank, once the hearing is over and it is safe for everyone to see. */
export function revealedAside(p: PublicState): number {
  if (p.phase !== 'over') return 0;
  for (let i = p.events.length - 1; i >= 0; i--) {
    const e = p.events[i];
    if (e.t === 'close') return e.aside;
  }
  return 0;
}

function clone(s: GameState): GameState {
  return {
    ...s,
    seats: s.seats.map((x) => ({ ...x, hand: x.hand.map((c) => ({ ...c })), discards: x.discards.slice() })),
    deck: s.deck.map((c) => ({ ...c })),
    aside: s.aside ? { ...s.aside } : null,
    open: s.open.slice(),
    winners: s.winners.slice(),
    log: s.log.slice(),
    events: s.events.slice(),
  };
}
